'use client';

import { useEffect } from 'react';
import { Info } from 'lucide-react';
import { UpdateFeedEntry, UpdateFeedType } from 'types/common';
import { markUpdateFeedSeen } from '#/lib/update-feed-seen';
import { cn } from '#/lib/utils';

interface UpdateFeedProps {
  entries: UpdateFeedEntry[];
}

const TYPE_LABEL: Record<UpdateFeedType, string> = {
  feat: '새 기능',
  fix: '수정',
  perf: '개선'
};

const TYPE_CLASS: Record<UpdateFeedType, string> = {
  feat: 'bg-[#104573]/10 text-[#104573] dark:bg-[#60a3d9]/15 dark:text-[#60a3d9]',
  fix: 'bg-amber-500/10 text-amber-700 dark:bg-amber-400/15 dark:text-amber-400',
  perf: 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-400'
};

function formatDate(date: string) {
  const [year, month, day] = date.split('-');
  return `${year}년 ${Number(month)}월 ${Number(day)}일`;
}

export default function UpdateFeed({ entries }: UpdateFeedProps) {
  // 피드를 열람하면 navbar 배지/미니 위젯의 "새 변경" 표시를 해제
  useEffect(() => {
    if (entries.length > 0) {
      markUpdateFeedSeen(entries[0].date);
    }
  }, [entries]);

  const groups: { date: string; items: UpdateFeedEntry[] }[] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last?.date === entry.date) {
      last.items.push(entry);
    } else {
      groups.push({ date: entry.date, items: [entry] });
    }
  }

  // 보여 줄 변경이 없으면 **안내문까지 통째로** 감춘다.
  //
  // 안내문은 "아래는 아직 공식 릴리즈 노트에 포함되지 않은 최근 변경 내역" 이라고
  // 말한다. 그 아래가 비어 있으면 없는 것을 있다고 예고하는 문장만 남아, 읽는 사람이
  // 무엇을 놓쳤나 하고 되짚게 된다. 정식 릴리즈 직후에는 비는 것이 정상이므로
  // (릴리즈 노트가 그 내용을 이미 담았다) 이 자리는 조용히 사라지는 편이 맞다.
  if (groups.length === 0) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-3 rounded-lg border border-[#104573]/20 bg-[#104573]/5 p-4 text-sm text-[#104573] dark:border-[#60a3d9]/25 dark:bg-[#60a3d9]/10 dark:text-[#cfe2f3]">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p className="font-semibold">공식 릴리즈 전 변경 사항입니다</p>
          <p>
            바른AI는 공식 릴리즈 전에도 맞춤법 교정·형태소 분석 개선 사항을
            서비스에 먼저 반영합니다. 아래는 아직 공식 릴리즈 노트에 포함되지
            않은 최근 변경 내역이며, 공식 릴리즈 시 릴리즈 노트로 정리됩니다.
          </p>
        </div>
      </div>

      {groups.map((group) => (
          <section key={group.date} className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground">
              {formatDate(group.date)}
            </h3>
            <ul className="space-y-2">
              {group.items.map((entry, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span
                    className={cn(
                      'mt-0.5 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4',
                      TYPE_CLASS[entry.type]
                    )}
                  >
                    {TYPE_LABEL[entry.type]}
                  </span>
                  <span className="text-sm leading-6">{entry.title}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
    </div>
  );
}
